import { captureMessage } from '@sentry/react';
import { signOut } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { Hub } from 'aws-amplify/utils';
import { useEffect, useState } from 'react';
import debug from '../services/debug';

// SWP-52: Amplify reports 'authenticated' from the stored tokens alone, but
// @saltus/auth loads the user from Cognito once and drops any error. When
// Cognito rejects the stored tokens (e.g. revoked) the page is stuck: a
// spinner in the app, an empty sign-in box in a browser.
//
// In the app web view the usual cause is the app's own Face ID re-login: it
// revokes the previous session, but the web view still holds that session's
// tokens in its cookies. Signing out here would post 'signedout' and make the
// app delete the stored Face ID credentials, so instead drop the stale tokens
// locally and post 'ready' to get the app's fresh ones.
export const RECOVERY_GRACE_MS = 3000;
// Recoveries allowed before giving up. Counted, not timed, so a slow reload
// can't slip past the guard and loop. The count survives the page reloads in
// between and is reset when a user loads or when it gives up, so the user's
// own refresh or app restart gets a fresh set of attempts.
export const MAX_RECOVERY_ATTEMPTS = 2;
export const RECOVERY_KEY = 'sessionRecoveryAttempts';

interface SessionRecoveryInput {
  authStatus: string;
  loading: boolean;
  onGiveUp: () => void;
  user: unknown;
}

const readAttempts = (): number => {
  try {
    return Number(window.sessionStorage.getItem(RECOVERY_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeAttempts = (value: number) => {
  try {
    if (value === 0) {
      window.sessionStorage.removeItem(RECOVERY_KEY);
    } else {
      window.sessionStorage.setItem(RECOVERY_KEY, String(value));
    }
  } catch {
    /* sessionStorage unavailable: the loop guard is best effort */
  }
};

const report = (message: string) =>
  captureMessage(message, {
    level: 'warning',
    tags: { webView: Boolean(window.ReactNativeWebView) },
  });

const askAppForTokens = async () => {
  try {
    await cognitoUserPoolsTokenProvider.tokenOrchestrator.clearTokens();
  } catch (error) {
    // The app's tokens overwrite these anyway, so carry on.
    debug('useSessionRecovery clearTokens failed', error);
  }

  window.ReactNativeWebView.postMessage('ready');
};

function useSessionRecovery({
  authStatus,
  loading,
  onGiveUp,
  user,
}: SessionRecoveryInput) {
  // Only a session restored from storage gets stuck this way. After a
  // sign-in on the page the tokens are fresh, and the user lookup that
  // follows can take longer than the grace period.
  const [signedInOnPage, setSignedInOnPage] = useState(false);

  useEffect(
    () =>
      Hub.listen('auth', ({ payload }) => {
        if (payload.event === 'signedIn') {
          setSignedInOnPage(true);
        }
      }),
    [],
  );

  useEffect(() => {
    if (user) {
      writeAttempts(0);
    }
  }, [user]);

  useEffect(() => {
    if (signedInOnPage || loading || authStatus !== 'authenticated' || user) {
      return undefined;
    }

    const giveUp = () => {
      report('Session recovery gave up');
      writeAttempts(0);
      onGiveUp();
    };

    const timer = setTimeout(() => {
      const attempts = readAttempts();

      // Recovering did not help: stop instead of looping.
      if (attempts >= MAX_RECOVERY_ATTEMPTS) {
        debug('useSessionRecovery giving up');
        giveUp();

        return;
      }

      writeAttempts(attempts + 1);
      report('Session recovery started');

      if (window.ReactNativeWebView) {
        debug('useSessionRecovery asking the app for tokens');
        askAppForTokens().catch((error) => {
          debug('useSessionRecovery asking the app failed', error);
          giveUp();
        });

        return;
      }

      debug('useSessionRecovery signing out');
      signOut().catch((error) => {
        debug('useSessionRecovery signOut failed', error);
        giveUp();
      });
    }, RECOVERY_GRACE_MS);

    return () => clearTimeout(timer);
  }, [authStatus, loading, onGiveUp, signedInOnPage, user]);
}

export default useSessionRecovery;
